import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useCase, useCaseDocuments, useDocumentChecklist, useCaseDocumentsSummary } from '../../hooks'
import {
  ArrowLeftIcon,
  DocumentIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  PlusCircleIcon,
  UserGroupIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  PhoneIcon,
  StarIcon,
  BookmarkIcon,
} from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import { formatDate, formatStatus, formatPhoneNumber } from '../../utils/formatters'
import { getStatusColor } from '../../utils/helpers'
import { ServiceAccordion } from '../../components/ui/Accordion'
import LoadingSpinner from '../../components/LoadingSpinner'
import DocumentUpload from '../../components/DocumentUpload'
import DocumentList from '../../components/DocumentList'
import { BankTransferPayment } from '../../components/payment/BankTransferPayment'
import { DocumentType } from '../../types'

function CaseDetailPage() {
  const { caseNumber } = useParams<{ caseNumber: string }>()
  const [activeTab, setActiveTab] = useState<'overview' | 'providers' | 'timeline' | 'documents' | 'payment'>('overview')

  const { data: caseData, isLoading: caseLoading, refetch: refetchCase } = useCase(caseNumber!)
  const { data: documents, isLoading: docsLoading } = useCaseDocuments(caseNumber!)
  const { data: checklist, isLoading: checklistLoading } = useDocumentChecklist(caseNumber!)
  const { data: summary, isLoading: summaryLoading } = useCaseDocumentsSummary(caseNumber!)

  
  // Debug logging when case data loads
  
  if (caseLoading || docsLoading || checklistLoading || summaryLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (!caseData) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-neutral-900">Case not found</h3>
        <p className="text-neutral-500">The case you're looking for doesn't exist.</p>
        <Link to="/cases" className="btn-primary mt-4">
          Back to Cases
        </Link>
      </div>
    )
  }

  
  const tabs = [
    { id: 'overview', label: 'Overview', icon: DocumentIcon },
    { id: 'providers', label: 'Providers', icon: UserGroupIcon },
    { id: 'timeline', label: 'Timeline', icon: CalendarIcon },
    { id: 'documents', label: `Documents ${summary?.totalDocuments || 0}`, icon: DocumentIcon },
    { id: 'payment', label: 'Payment', icon: CurrencyDollarIcon },
  ]

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Link
            to="/cases"
            className="mr-4 p-2 text-neutral-400 hover:text-neutral-600"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">
              Case {caseData.caseNumber}
            </h1>
            <p className="text-sm text-neutral-600">
              {caseData.deceasedName} • {formatDate(caseData.createdAt)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(caseData.status)}`}>
            {formatStatus(caseData.status)}
          </span>

          {caseData.status === 'DOCUMENTS_PENDING' && (
            <div className="flex items-center text-sm text-amber-600">
              <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
              Documents required to proceed
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-neutral-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`
                  py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2
                  ${activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
                  }
                `}
              >
                <Icon className="h-5 w-5" />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          {/* Case Summary */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Deceased Information */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-neutral-900">Deceased Information</h3>
              </div>
              <div className="card-body space-y-3">
                <div>
                  <p className="text-sm font-medium text-neutral-700">Full Name</p>
                  <p className="text-sm text-neutral-900">{caseData.deceasedName}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-neutral-700">Gender</p>
                  <p className="text-sm text-neutral-900">{caseData.deceasedGender}</p>
                </div>
                {caseData.deceasedAge && (
                  <div>
                    <p className="text-sm font-medium text-neutral-700">Age</p>
                    <p className="text-sm text-neutral-900">{caseData.deceasedAge} years</p>
                  </div>
                )}
                {caseData.deceasedIC && (
                  <div>
                    <p className="text-sm font-medium text-neutral-700">IC Number</p>
                    <p className="text-sm text-neutral-900">{caseData.deceasedIC}</p>
                  </div>
                )}
                {caseData.placeOfDeath && (
                  <div>
                    <p className="text-sm font-medium text-neutral-700">Place of Death</p>
                    <p className="text-sm text-neutral-900">{caseData.placeOfDeath}</p>
                  </div>
                )}
                {caseData.timeOfDeath && (
                  <div>
                    <p className="text-sm font-medium text-neutral-700">Time of Death</p>
                    <p className="text-sm text-neutral-900">{formatDate(caseData.timeOfDeath)}</p>
                  </div>
                )}
                {caseData.causeOfDeath && (
                  <div>
                    <p className="text-sm font-medium text-neutral-700">Cause of Death</p>
                    <p className="text-sm text-neutral-900">{caseData.causeOfDeath}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-neutral-900">Contact Information</h3>
              </div>
              <div className="card-body space-y-3">
                <div>
                  <p className="text-sm font-medium text-neutral-700">Primary Contact</p>
                  <p className="text-sm text-neutral-900">{caseData.primaryContact}</p>
                  <p className="text-sm text-neutral-600">{formatPhoneNumber(caseData.primaryContactPhone)}</p>
                </div>
                {caseData.secondaryContact && (
                  <div>
                    <p className="text-sm font-medium text-neutral-700">Secondary Contact</p>
                    <p className="text-sm text-neutral-900">{caseData.secondaryContact}</p>
                    <p className="text-sm text-neutral-600">{formatPhoneNumber(caseData.secondaryContactPhone!)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Burial Preferences */}
          {(caseData.preferredBurialDate || caseData.preferredBurialTime || caseData.mosquePreference || caseData.burialCemetery) && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-neutral-900">Burial Preferences</h3>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {caseData.preferredBurialDate && (
                    <div>
                      <p className="text-sm font-medium text-neutral-700">Preferred Burial Date</p>
                      <p className="text-sm text-neutral-900">{formatDate(caseData.preferredBurialDate)}</p>
                    </div>
                  )}
                  {caseData.preferredBurialTime && (
                    <div>
                      <p className="text-sm font-medium text-neutral-700">Preferred Time</p>
                      <p className="text-sm text-neutral-900">{caseData.preferredBurialTime}</p>
                    </div>
                  )}
                  {caseData.mosquePreference && (
                    <div>
                      <p className="text-sm font-medium text-neutral-700">Mosque Preference</p>
                      <p className="text-sm text-neutral-900">{caseData.mosquePreference}</p>
                    </div>
                  )}
                  {caseData.burialCemetery && (
                    <div>
                      <p className="text-sm font-medium text-neutral-700">Preferred Cemetery</p>
                      <p className="text-sm text-neutral-900">{caseData.burialCemetery}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Document Summary */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-neutral-900">Document Status</h3>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  <span className="text-sm text-neutral-600">
                    {summary?.verifiedRequired || 0}/{summary?.requiredDocuments || 0} required documents verified
                  </span>
                </div>
                <div className="text-sm text-neutral-500">
                  ({summary?.compliancePercentage || 0}% complete)
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-neutral-900">{summary?.totalDocuments || 0}</div>
                  <div className="text-sm text-neutral-600">Total Documents</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{summary?.verifiedDocuments || 0}</div>
                  <div className="text-sm text-neutral-600">Verified</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{summary?.uploadedDocuments || 0}</div>
                  <div className="text-sm text-neutral-600">Pending Review</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">{summary?.pendingDocuments || 0}</div>
                  <div className="text-sm text-neutral-600">Required</div>
                </div>
              </div>

              {summary?.pendingDocuments && summary.pendingDocuments > 0 && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
                    <p className="text-sm text-yellow-800">
                      {summary.pendingDocuments} required document(s) still needed. Upload them to proceed with funeral arrangements.
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-4">
                <button
                  onClick={() => setActiveTab('documents')}
                  className="btn-outline w-full"
                >
                  Manage Documents
                </button>
              </div>
            </div>
          </div>

          {/* Bookings */}
          {caseData.bookings && caseData.bookings.length > 0 && (
            <div className="card" id="service-bookings-section">
              <div className="card-header">
                <h3 className="text-lg font-medium text-neutral-900">Service Bookings</h3>
              </div>
              <div className="card-body">
                <div className="space-y-3">
                  {caseData.bookings.map((booking) => (
                    <div key={booking.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-neutral-900">
                          {booking.serviceType.replace('_', ' ')}
                        </p>
                        <p className="text-xs text-neutral-600">
                          {booking.serviceProvider?.companyName || `${booking.serviceProvider?.user?.firstName || ''} ${booking.serviceProvider?.user?.lastName || ''}`.trim() || 'Service Provider'}
                        </p>
                        <p className="text-xs text-neutral-500">
                          {formatDate(booking.scheduledDateTime)}
                        </p>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        booking.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                        booking.status === 'CONFIRMED' ? 'bg-blue-100 text-blue-800' :
                        booking.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {booking.status.replace('_', ' ').toLowerCase()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'providers' && (
        <div className="space-y-8">
          {/* Provider Booking Status Banner */}
          {caseData.providersSkipped && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mr-3 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-amber-800">Provider Booking Skipped</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    You chose to skip provider booking during case creation. You can book providers now or return to this later from your case dashboard.
                  </p>
                  <div className="mt-3 flex space-x-3">
                    <button className="btn-primary text-sm">
                      Book Providers Now
                    </button>
                    <button className="btn-secondary text-sm">
                      Remind Me Later
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Provider Booking Summary */}
          {!caseData.providersSkipped && caseData.bookings && caseData.bookings.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-3 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-green-800">Providers Booked</h3>
                  <p className="text-sm text-green-700 mt-1">
                    {caseData.bookings.length} provider(s) have been successfully booked for this case.
                  </p>
                  <div className="mt-2">
                    <button
                      onClick={() => {
                        // Switch to overview tab if not already there
                        if (activeTab !== 'overview') {
                          setActiveTab('overview');
                        }
                        // Scroll to bookings section
                        setTimeout(() => {
                          const element = document.getElementById('service-bookings-section');
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth' });
                          }
                        }, 100);
                      }}
                      className="btn-secondary text-sm"
                    >
                      View Bookings
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Service Breakdown */}
          {caseData.serviceBreakdown && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-neutral-900 flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 mr-2 text-primary-600" />
                  Service Breakdown & Costs
                </h3>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Services List - Enhanced with Accordion */}
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-4">Selected Services</h4>
                    {caseData.serviceBreakdown.services?.length > 0 ? (
                      <ServiceAccordion
                        services={caseData.serviceBreakdown.services}
                        className="space-y-2"
                      />
                    ) : (
                      <p className="text-sm text-neutral-500 italic">No services selected yet</p>
                    )}
                  </div>

                  {/* Cost Summary */}
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-4">Cost Summary</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-600">Subtotal:</span>
                        <span className="font-medium">RM {caseData.serviceBreakdown.estimatedCosts?.subtotal || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-600">Additional Fees:</span>
                        <span className="font-medium">RM {caseData.serviceBreakdown.estimatedCosts?.additional || 0}</span>
                      </div>
                      <div className="pt-2 border-t border-neutral-200">
                        <div className="flex justify-between">
                          <span className="font-semibold text-neutral-900">Total Cost:</span>
                          <span className="font-bold text-lg text-primary-600">
                            RM {caseData.serviceBreakdown.estimatedCosts?.total || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Provider Matching Results */}
          {caseData.matchedProviders && (
            <div className="card">
              <div className="card-header">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-medium text-neutral-900 flex items-center">
                      <UserGroupIcon className="h-5 w-5 mr-2 text-primary-600" />
                      Available Service Providers
                    </h3>
                    <p className="text-sm text-neutral-500 mt-1">
                      {caseData.providersSkipped
                        ? "Recommended providers based on your requirements. Select providers to book them for this case."
                        : caseData.bookings && caseData.bookings.length > 0
                        ? "Your selected service providers for this case."
                        : "Available service providers for your funeral arrangements"}
                    </p>
                  </div>
                  {caseData.providersSkipped ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      Action Required
                    </span>
                  ) : caseData.bookings && caseData.bookings.length > 0 ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Providers Selected
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="card-body">
                {/* Helper function to filter providers based on case state */}
                {(() => {
                  const shouldFilterProviders = !caseData.providersSkipped &&
                    (!caseData.bookings || caseData.bookings.length === 0);

                  const getSelectedProviderIds = () => {
                    const selectedIds = new Set();
                    // Get provider IDs from serviceBreakdown if available
                    if (caseData.serviceBreakdown?.selectedProviders) {
                      caseData.serviceBreakdown.selectedProviders.forEach((p: any) => {
                        selectedIds.add(p.id);
                      });
                    }
                    // Also get from bookings if available
                    if (caseData.bookings) {
                      caseData.bookings.forEach((booking: any) => {
                        if (booking.providerId) selectedIds.add(booking.providerId);
                        if (booking.serviceProvider?.id) selectedIds.add(booking.serviceProvider.id);
                      });
                    }
                    return selectedIds;
                  };

                  return null; // This is just to define the helper
                })()}

                {/* Ambulance Providers */}
                {caseData.matchedProviders.ambulance && (() => {
                  const shouldFilterProviders = !caseData.providersSkipped &&
                    (!caseData.bookings || caseData.bookings.length === 0);

                  const getSelectedProviderIds = () => {
                    const selectedIds = new Set();
                    if (caseData.serviceBreakdown?.selectedProviders) {
                      caseData.serviceBreakdown.selectedProviders.forEach((p: any) => {
                        selectedIds.add(p.id);
                      });
                    }
                    if (caseData.bookings) {
                      caseData.bookings.forEach((booking: any) => {
                        if (booking.providerId) selectedIds.add(booking.providerId);
                        if (booking.serviceProvider?.id) selectedIds.add(booking.serviceProvider.id);
                      });
                    }
                    return selectedIds;
                  };

                  let providers = caseData.matchedProviders.ambulance.providers || [];
                  if (shouldFilterProviders) {
                    const selectedIds = getSelectedProviderIds();
                    providers = providers.filter((provider: any) => selectedIds.has(provider.id));
                  }

                  // Don't render the section if no providers after filtering
                  if (providers.length === 0) return null;

                  return (
                    <div className="mb-6">
                      <h4 className="font-medium text-neutral-900 mb-3 flex items-center">
                        <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                        Ambulance Services ({shouldFilterProviders ? providers.length : (caseData.matchedProviders.ambulance.matchedCount || 0)} {shouldFilterProviders ? 'selected' : 'found'})
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {providers.slice(0, 4).map((provider, index) => (
                        <div key={index} className="border border-neutral-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h5 className="font-medium text-neutral-900">{provider.companyName || 'Service Provider'}</h5>
                              <p className="text-sm text-neutral-600">{provider.serviceType?.replace('_', ' ') || 'Service'}</p>
                            </div>
                            {provider.rating && (
                              <div className="flex items-center text-sm">
                                <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                                <span>{provider.rating}</span>
                              </div>
                            )}
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center text-sm text-neutral-600">
                              <MapPinIcon className="h-4 w-4 mr-1" />
                              {[provider.city, provider.state].filter(Boolean).join(', ') || 'Location not available'}
                            </div>
                            {provider.distance && (
                              <div className="text-sm text-neutral-600">
                                Distance: {provider.distance} km
                              </div>
                            )}
                            {provider.estimatedArrival && (
                              <div className="text-sm text-neutral-600">
                                ETA: {provider.estimatedArrival}
                              </div>
                            )}
                            {provider.phone && (
                              <div className="flex items-center text-sm text-neutral-600">
                                <PhoneIcon className="h-4 w-4 mr-1" />
                                {provider.phone}
                              </div>
                            )}
                          </div>
                          <div className="mt-3 flex justify-between items-center">
                            {provider.pricing?.baseRate && (
                              <span className="text-sm font-semibold text-primary-600">
                                From RM {provider.pricing.baseRate}
                              </span>
                            )}
                            {/* Check if this provider is already booked */}
                            {(() => {
                              // Check both bookings and selected providers from workflow
                              const isProviderBooked = caseData.bookings?.some(booking =>
                                booking.service_providers?.id === provider.id ||
                                booking.service_providers?.companyName === provider.companyName
                              );

                              // Check if providers were selected during workflow (stored in serviceBreakdown.selectedProviders)
                              // This is the correct data structure that contains only the actually selected providers
                              const wasProviderSelected = caseData.serviceBreakdown?.selectedProviders?.some((selectedProvider: any) =>
                                selectedProvider.companyName === provider.companyName ||
                                selectedProvider.id === provider.id
                              ) || false;

                              const isProviderSelected = isProviderBooked || wasProviderSelected;

                              if (isProviderBooked) {
                                return (
                                  <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                    ✓ Booked
                                  </span>
                                );
                              } else if (wasProviderSelected) {
                                return (
                                  <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                    ✓ Selected
                                  </span>
                                );
                              } else if (caseData.providersSkipped) {
                                return (
                                  <button className="text-sm btn-primary">
                                    Book Provider
                                  </button>
                                );
                              } else {
                                return (
                                  <button className="text-sm btn-secondary" disabled>
                                    Select Provider
                                  </button>
                                );
                              }
                            })()}
                          </div>
                        </div>
                      ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Ustadz Providers */}
                {caseData.matchedProviders.ustadz && (
                  <div className="mb-6">
                    <h4 className="font-medium text-neutral-900 mb-3 flex items-center">
                      <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                      Ustadz Services ({caseData.matchedProviders.ustadz.matchedCount || 0} found)
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {caseData.matchedProviders.ustadz.providers?.slice(0, 4).map((provider, index) => (
                        <div key={index} className="border border-neutral-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h5 className="font-medium text-neutral-900">{provider.companyName || 'Service Provider'}</h5>
                              <p className="text-sm text-neutral-600">{provider.serviceType?.replace('_', ' ') || 'Service'}</p>
                            </div>
                            {provider.rating && (
                              <div className="flex items-center text-sm">
                                <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                                <span>{provider.rating}</span>
                              </div>
                            )}
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center text-sm text-neutral-600">
                              <MapPinIcon className="h-4 w-4 mr-1" />
                              {[provider.city, provider.state].filter(Boolean).join(', ') || 'Location not available'}
                            </div>
                            {provider.expertise && (
                              <div className="text-sm text-neutral-600">
                                Expertise: {provider.expertise.join(', ')}
                              </div>
                            )}
                            {provider.phone && (
                              <div className="flex items-center text-sm text-neutral-600">
                                <PhoneIcon className="h-4 w-4 mr-1" />
                                {provider.phone}
                              </div>
                            )}
                          </div>
                          <div className="mt-3 flex justify-between items-center">
                            {provider.pricing?.baseRate && (
                              <span className="text-sm font-semibold text-primary-600">
                                From RM {provider.pricing.baseRate}
                              </span>
                            )}
                            {/* Check if this provider is already booked */}
                            {(() => {
                              // Check both bookings and selected providers from workflow
                              const isProviderBooked = caseData.bookings?.some(booking =>
                                booking.service_providers?.id === provider.id ||
                                booking.service_providers?.companyName === provider.companyName
                              );

                              // Check if providers were selected during workflow (stored in serviceBreakdown.selectedProviders)
                              // This is the correct data structure that contains only the actually selected providers
                              const wasProviderSelected = caseData.serviceBreakdown?.selectedProviders?.some((selectedProvider: any) =>
                                selectedProvider.companyName === provider.companyName ||
                                selectedProvider.id === provider.id
                              ) || false;

                              const isProviderSelected = isProviderBooked || wasProviderSelected;

                              if (isProviderBooked) {
                                return (
                                  <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                    ✓ Booked
                                  </span>
                                );
                              } else if (wasProviderSelected) {
                                return (
                                  <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                    ✓ Selected
                                  </span>
                                );
                              } else if (caseData.providersSkipped) {
                                return (
                                  <button className="text-sm btn-primary">
                                    Book Provider
                                  </button>
                                );
                              } else {
                                return (
                                  <button className="text-sm btn-secondary" disabled>
                                    Select Provider
                                  </button>
                                );
                              }
                            })()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Burial Ground Providers */}
                {caseData.matchedProviders.burialGrounds && (
                  <div className="mb-6">
                    <h4 className="font-medium text-neutral-900 mb-3 flex items-center">
                      <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                      Burial Grounds ({caseData.matchedProviders.burialGrounds.matchedCount || 0} found)
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {caseData.matchedProviders.burialGrounds.providers?.slice(0, 4).map((provider, index) => (
                        <div key={index} className="border border-neutral-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h5 className="font-medium text-neutral-900">{provider.companyName || 'Service Provider'}</h5>
                              <p className="text-sm text-neutral-600">{provider.serviceType?.replace('_', ' ') || 'Service'}</p>
                            </div>
                            {provider.rating && (
                              <div className="flex items-center text-sm">
                                <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                                <span>{provider.rating}</span>
                              </div>
                            )}
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center text-sm text-neutral-600">
                              <MapPinIcon className="h-4 w-4 mr-1" />
                              {[provider.city, provider.state].filter(Boolean).join(', ') || 'Location not available'}
                            </div>
                            {provider.muslimSectionAvailable && (
                              <div className="text-sm text-green-600 font-medium">
                                ✓ Muslim Section Available
                              </div>
                            )}
                            {provider.availablePlots && (
                              <div className="text-sm text-neutral-600">
                                {provider.availablePlots} plots available
                              </div>
                            )}
                            {provider.phone && (
                              <div className="flex items-center text-sm text-neutral-600">
                                <PhoneIcon className="h-4 w-4 mr-1" />
                                {provider.phone}
                              </div>
                            )}
                          </div>
                          <div className="mt-3 flex justify-between items-center">
                            {provider.pricing?.plotPrice && (
                              <span className="text-sm font-semibold text-primary-600">
                                From RM {provider.pricing.plotPrice}
                              </span>
                            )}
                            {/* Check if this provider is already booked */}
                            {(() => {
                              // Check both bookings and selected providers from workflow
                              const isProviderBooked = caseData.bookings?.some(booking =>
                                booking.service_providers?.id === provider.id ||
                                booking.service_providers?.companyName === provider.companyName
                              );

                              // Check if providers were selected during workflow (stored in serviceBreakdown.selectedProviders)
                              // This is the correct data structure that contains only the actually selected providers
                              const wasProviderSelected = caseData.serviceBreakdown?.selectedProviders?.some((selectedProvider: any) =>
                                selectedProvider.companyName === provider.companyName ||
                                selectedProvider.id === provider.id
                              ) || false;

                              const isProviderSelected = isProviderBooked || wasProviderSelected;

                              if (isProviderBooked) {
                                return (
                                  <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                    ✓ Booked
                                  </span>
                                );
                              } else if (wasProviderSelected) {
                                return (
                                  <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                    ✓ Selected
                                  </span>
                                );
                              } else if (caseData.providersSkipped) {
                                return (
                                  <button className="text-sm btn-primary">
                                    Book Provider
                                  </button>
                                );
                              } else {
                                return (
                                  <button className="text-sm btn-secondary" disabled>
                                    Select Provider
                                  </button>
                                );
                              }
                            })()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {caseData.matchedProviders.recommendations && caseData.matchedProviders.recommendations.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-medium text-neutral-900 mb-3">Our Recommendations</h4>
                    <div className="space-y-3">
                      {caseData.matchedProviders.recommendations.map((rec, index) => (
                        <div key={index} className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
                          <div className="flex items-start">
                            <BookmarkIcon className="h-5 w-5 text-primary-600 mr-3 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-neutral-900">{rec.provider}</p>
                              <p className="text-sm text-neutral-600">{rec.reason}</p>
                              {rec.action && (
                                <button className="btn-primary text-sm mt-2">
                                  {rec.action}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* No Provider Data Message */}
          {!caseData.matchedProviders && !caseData.serviceBreakdown && (
            <div className="card">
              <div className="card-body text-center py-8">
                <UserGroupIcon className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-neutral-900 mb-2">No Provider Matching Data</h3>
                <p className="text-sm text-neutral-600 mb-4">
                  Provider matching information will be available once you complete the provider selection workflow.
                </p>
                <button className="btn-primary">
                  Start Provider Matching
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'timeline' && (
        <div className="space-y-8">
          {/* Generated Timeline */}
          {caseData.generatedTimeline && caseData.generatedTimeline.length > 0 ? (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-neutral-900 flex items-center">
                  <CalendarIcon className="h-5 w-5 mr-2 text-primary-600" />
                  Funeral Arrangement Timeline
                </h3>
                <p className="text-sm text-neutral-500 mt-1">
                  Automated schedule based on urgency level and selected services
                </p>
              </div>
              <div className="card-body">
                <div className="relative">
                  {/* Timeline Line */}
                  <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-neutral-200"></div>

                  {/* Timeline Items */}
                  <div className="space-y-6">
                    {caseData.generatedTimeline.map((item, index) => (
                      <div key={item.id || index} className="relative flex items-start">
                        {/* Timeline Dot */}
                        <div className={`
                          relative z-10 flex items-center justify-center w-12 h-12 rounded-full
                          ${item.status === 'completed' ? 'bg-green-100 text-green-600' :
                            item.status === 'in-progress' ? 'bg-yellow-100 text-yellow-600' :
                            'bg-neutral-100 text-neutral-400'}
                        `}>
                          {item.type === 'assessment' && <CheckCircleIcon className="h-6 w-6" />}
                          {item.type === 'providers' && <UserGroupIcon className="h-6 w-6" />}
                          {item.type === 'logistics' && <PhoneIcon className="h-6 w-6" />}
                          {item.type === 'preparation' && <ClockIcon className="h-6 w-6" />}
                          {item.type === 'religious' && <BookmarkIcon className="h-6 w-6" />}
                          {item.type === 'burial' && <MapPinIcon className="h-6 w-6" />}
                        </div>

                        {/* Timeline Content */}
                        <div className="ml-6 flex-1">
                          <div className={`p-4 rounded-lg border ${
                            item.status === 'completed' ? 'bg-green-50 border-green-200' :
                            item.status === 'in-progress' ? 'bg-yellow-50 border-yellow-200' :
                            'bg-neutral-50 border-neutral-200'
                          }`}>
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="font-medium text-neutral-900">{item.title}</h4>
                              {item.timestamp && (
                                <span className="text-sm text-neutral-600">
                                  {formatDate(item.timestamp)}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-neutral-600">{item.description}</p>
                            {item.status === 'completed' && (
                              <div className="mt-2 flex items-center text-sm text-green-600">
                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                                Completed
                              </div>
                            )}
                            {item.status === 'in-progress' && (
                              <div className="mt-2 flex items-center text-sm text-yellow-600">
                                <ClockIcon className="h-4 w-4 mr-1" />
                                In Progress
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body text-center py-8">
                <CalendarIcon className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-neutral-900 mb-2">No Timeline Available</h3>
                <p className="text-sm text-neutral-600 mb-4">
                  Timeline will be generated once you complete the assessment and provider selection.
                </p>
                <button className="btn-primary">
                  Complete Assessment
                </button>
              </div>
            </div>
          )}

          {/* Payment Schedule - COMMENTED OUT FOR FUTURE USE */}
          {/* {caseData.serviceBreakdown?.paymentSchedule && caseData.serviceBreakdown.paymentSchedule.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-neutral-900 flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 mr-2 text-primary-600" />
                  Payment Schedule
                </h3>
                <p className="text-sm text-neutral-500 mt-1">
                  Payment milestones for your funeral arrangement services
                </p>
              </div>
              <div className="card-body">
                <div className="space-y-4">
                  {caseData.serviceBreakdown.paymentSchedule.map((payment, index) => (
                    <div key={payment.id || index} className="flex items-center justify-between p-4 border border-neutral-200 rounded-lg">
                      <div className="flex items-center">
                        <div className={`
                          w-10 h-10 rounded-full flex items-center justify-center mr-4
                          ${payment.status === 'completed' ? 'bg-green-100 text-green-600' :
                            payment.status === 'pending' ? 'bg-yellow-100 text-yellow-600' :
                            'bg-neutral-100 text-neutral-400'}
                        `}>
                          <CurrencyDollarIcon className="h-5 w-5" />
                        </div>
                        <div>
                          <h4 className="font-medium text-neutral-900">{payment.name}</h4>
                          <p className="text-sm text-neutral-600">{payment.description}</p>
                          <p className="text-xs text-neutral-500">
                            Due: {formatDate(payment.dueDate)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-primary-600">RM {payment.amount}</p>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                          payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {payment.status.replace('_', ' ').toLowerCase()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )} */}
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="space-y-8">
          {/* Document Checklist */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-neutral-900">Required Documents</h3>
              <p className="text-sm text-neutral-500 mt-1">
                Upload all required documents to proceed with funeral arrangements
              </p>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                {checklist?.requiredDocuments
                  ?.filter((doc) => doc.required)
                  ?.map((doc) => {
                    let statusIcon
                    let statusColor
                    let statusText

                    if (doc.status === 'VERIFIED') {
                      statusIcon = <CheckCircleIcon className="h-5 w-5" />
                      statusColor = 'text-green-600'
                      statusText = 'Verified'
                    } else if (doc.uploaded) {
                      statusIcon = <ClockIcon className="h-5 w-5" />
                      statusColor = 'text-yellow-600'
                      statusText = 'Pending Review'
                    } else {
                      statusIcon = <ExclamationTriangleIcon className="h-5 w-5" />
                      statusColor = 'text-red-600'
                      statusText = 'Required'
                    }

                    return (
                      <div key={doc.type} className="flex items-center justify-between p-3 border border-neutral-200 rounded-lg">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-neutral-900">{doc.name}</p>
                          <p className="text-xs text-neutral-500">Required for funeral arrangements</p>
                        </div>
                        <div className={`flex items-center gap-2 ${statusColor}`}>
                          {statusIcon}
                          <span className="text-sm font-medium">{statusText}</span>
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>
          </div>

          {/* Upload Documents */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-neutral-900">Upload Documents</h3>
              <span className="text-sm text-neutral-500">
                Supported formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB)
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[
                DocumentType.MEDICAL_CERTIFICATE,
                DocumentType.BURIAL_PERMIT,
                DocumentType.IDENTIFICATION,
                DocumentType.CONSENT_FORM,
              ]
                .filter((type) => {
                  // Only show upload if document doesn't exist or is not verified
                  const existing = checklist?.requiredDocuments?.find((doc) => doc.type === type)
                  return !existing || existing.status !== 'VERIFIED'
                })
                .map((type) => (
                  <DocumentUpload
                    key={type}
                    caseId={caseData.id}
                    documentType={type}
                    onUploadSuccess={() => {
                      // Refetch documents
                      window.location.reload()
                    }}
                  />
                ))}
            </div>
          </div>

          {/* Uploaded Documents */}
          <div>
            <h3 className="text-lg font-medium text-neutral-900 mb-4">Uploaded Documents</h3>
            <DocumentList
              documents={documents}
              onDocumentUpdated={() => {
                // Refetch documents
                window.location.reload()
              }}
            />
          </div>
        </div>
      )}

      {activeTab === 'payment' && (
        <BankTransferPayment
          caseId={caseData.id}
          caseNumber={caseData.caseNumber}
          totalAmount={
            // For ambulance bookings, get pricing from booking data
            (() => {
              // Check if this is an ambulance case with bookings
              if (caseData.bookings && caseData.bookings.length > 0) {
                const ambulanceBooking = caseData.bookings.find(booking =>
                  booking.serviceType === 'AMBULANCE_TRANSPORT' &&
                  booking.pricing
                );
                if (ambulanceBooking && ambulanceBooking.pricing) {
                  // Parse the pricing JSON to get the total amount
                  const pricing = typeof ambulanceBooking.pricing === 'string'
                    ? JSON.parse(ambulanceBooking.pricing)
                    : ambulanceBooking.pricing;
                  return pricing.total || pricing.amount || 0;
                }
              }
              // Fallback to regular case pricing for non-ambulance cases
              return caseData.serviceBreakdown?.estimatedCosts?.total || caseData.estimatedTotal || 0;
            })()
          }
          payments={caseData.payments}
          onPaymentComplete={() => {
            // Ensure we stay on Payment tab after payment completion
            setActiveTab('payment')
            // Refetch case data to update payment information without page reload
            refetchCase()
          }}
        />
      )}
    </div>
  )
}

export default CaseDetailPage